import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Receivables,
  getLocationStoreName,
  getReceivablesLocation,
  queryKeys,
} from '@vgrubs/components';

/**
 * One location's receivables: the balance as the merchant sees it (plus the
 * old vDrive items) and every item it ever had. Each write on the page answers
 * with this same object and goes straight into this key.
 */
export const useReceivablesLocation = (locationId: number) => {
  const query = useQuery({
    queryKey: queryKeys.admin.receivables.location(locationId),
    queryFn: async () => {
      const { data } = await getReceivablesLocation(locationId);
      return data.data;
    },
    enabled: Number.isFinite(locationId),
  });

  return { detail: query.data ?? null, isLoading: query.isLoading };
};

/**
 * Who the location is. The detail answer does not say, so the list row it was
 * opened from is used when it is cached; opened from anywhere else (the vDrive
 * screen's link, a reload), the name is looked up - through the menu's name
 * route, brand 0 meaning the location itself. Not `verified-by-ids`: that one
 * leaves out some locations that do owe (it answered [] for location 9).
 */
export const useReceivablesLocationName = (locationId: number) => {
  const queryClient = useQueryClient();
  // Any filter of the list will do; "all" has every location.
  const fromList = queryClient
    .getQueriesData<Receivables>({ queryKey: queryKeys.admin.receivables.list })
    .flatMap(([, data]) => data?.locations ?? [])
    .find((row) => row.locationId === locationId);

  const query = useQuery({
    queryKey: queryKeys.admin.receivables.locationName(locationId),
    queryFn: async () => {
      const { data } = await getLocationStoreName(locationId, 0);
      return data.data.locationName ?? null;
    },
    enabled: !fromList && Number.isFinite(locationId),
    staleTime: Infinity,
  });

  return {
    name: fromList?.locationName ?? query.data ?? null,
    ownerName: fromList?.ownerName ?? null,
  };
};
